/**
 * 🧭 HelpMobileSheet — Aide & Navigation CoachDIA (mobile).
 *
 * Bottom sheet plein écran · accordéon par section · cartes tactiles larges.
 * Version condensée : chaque item tient sur 2-3 lignes.
 */
import React, { useEffect, useState } from 'react';
import { X, HelpCircle, ChevronDown } from 'lucide-react';
import { NAVIGATION_HELP_SECTIONS, NAVIGATION_HELP_GLOSSARY, filterHelpItems } from '../../config/navigationHelp';

const T = {
  bg: '#FBF9F3', card: '#FFFFFF', ink: '#1C1147',
  text: '#1F2937', muted: '#6B7280',
  border: '#E8E2D2', gold: '#D4AF37', goldDeep: '#B7901F', goldLight: '#F3D060',
  ivoryDeep: '#FFFCEC', violet: '#7A4FCF', violetSoft: '#F4EFFF',
};

function HighlightMobile({ h }) {
  if (!h) return null;
  return (
    <div
      className="rounded-2xl p-4 mb-3"
      style={{
        background: `linear-gradient(135deg, ${T.ivoryDeep} 0%, ${T.card} 60%, ${T.ivoryDeep} 100%)`,
        border: `1.5px solid ${T.gold}`,
      }}
    >
      <h4 className="text-sm mb-1" style={{ color: T.ink, fontFamily: 'Georgia, serif', fontWeight: 700 }}>
        {h.title}
      </h4>
      <p className="text-xs mb-2" style={{ color: T.text }}>{h.body}</p>
      {h.bullets && (
        <div className="space-y-1.5">
          {h.bullets.map((b, i) => {
            const Icon = b.icon;
            return (
              <div key={i} className="flex items-center gap-2">
                <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: T.violet }} />
                <span className="text-xs font-bold" style={{ color: T.ink }}>{b.label}</span>
                <span className="text-xs" style={{ color: T.muted }}>· {b.desc}</span>
              </div>
            );
          })}
        </div>
      )}
      {h.badge && (
        <div className="mt-2 inline-flex px-2.5 py-0.5 rounded-full text-[10px] font-bold"
          style={{ background: T.ink, color: T.goldLight }}>
          {h.badge}
        </div>
      )}
    </div>
  );
}

function ItemMobile({ item }) {
  const Icon = item.icon;
  return (
    <div
      className="p-3 rounded-xl"
      style={{ background: T.card, border: `1px solid ${T.border}` }}
      data-testid={`help-mobile-item-${item.id}`}
    >
      <div className="flex items-start gap-3">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: T.ivoryDeep, border: `1px solid ${T.border}` }}
        >
          <Icon className="w-4 h-4" style={{ color: T.goldDeep }} />
        </div>
        <div className="min-w-0 flex-1">
          <h4 className="text-sm mb-1" style={{ color: T.ink, fontFamily: 'Georgia, serif', fontWeight: 700 }}>
            {item.label}
          </h4>
          <p className="text-xs mb-0.5" style={{ color: T.text }}>{item.what}</p>
          <p className="text-[11px]" style={{ color: T.muted }}>
            <span className="font-semibold" style={{ color: T.goldDeep }}>Utilité · </span>{item.use}
          </p>
        </div>
      </div>
    </div>
  );
}

function Accordion({ section, open, onToggle }) {
  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{ background: T.bg, border: `1px solid ${T.border}` }}
    >
      <button
        type="button"
        onClick={onToggle}
        data-testid={`help-mobile-accordion-${section.id}`}
        className="w-full px-4 py-3.5 flex items-center justify-between gap-3 text-left"
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-xl" aria-hidden>{section.emoji}</span>
          <div className="min-w-0">
            <div className="text-sm" style={{ color: T.ink, fontFamily: 'Georgia, serif', fontWeight: 700 }}>
              {section.title}
            </div>
            <div className="text-[11px] truncate" style={{ color: T.muted }}>{section.subtitle}</div>
          </div>
        </div>
        <ChevronDown
          className="w-5 h-5 flex-shrink-0 transition-transform"
          style={{
            color: T.goldDeep,
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 space-y-2">
          <HighlightMobile h={section.highlight} />
          {section.items.map((it) => <ItemMobile key={it.id} item={it} />)}
        </div>
      )}
    </div>
  );
}

export default function HelpMobileSheet({ open, onClose, userContext = {} }) {
  const [openId, setOpenId] = useState('demarrer');

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;
  const sections = filterHelpItems(NAVIGATION_HELP_SECTIONS, userContext);

  return (
    <div
      data-testid="help-mobile-sheet"
      className="fixed inset-0 z-[10005] flex"
      style={{ background: 'rgba(28,17,71,0.6)', backdropFilter: 'blur(10px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        className="relative w-full mt-6 rounded-t-3xl overflow-hidden flex flex-col"
        style={{
          background: T.bg,
          border: `1px solid ${T.border}`,
          boxShadow: '0 -20px 60px -10px rgba(28,17,71,0.4)',
        }}
      >
        {/* Handle */}
        <div className="pt-2 pb-1 flex justify-center">
          <div className="w-10 h-1 rounded-full" style={{ background: T.border }} />
        </div>

        {/* Header */}
        <div
          className="flex-shrink-0 px-5 pb-3 flex items-start justify-between gap-3"
          style={{ borderBottom: `1px solid ${T.border}` }}
        >
          <div>
            <div className="flex items-center gap-2">
              <HelpCircle className="w-3.5 h-3.5" style={{ color: T.goldDeep }} />
              <span
                className="text-[9px] uppercase tracking-[0.22em] font-bold"
                style={{ color: T.goldDeep }}
              >
                Aide & Navigation
              </span>
            </div>
            <h2
              className="text-xl mt-0.5"
              style={{ color: T.ink, fontFamily: 'Georgia, serif', fontWeight: 600 }}
            >
              Guide CoachDIA
            </h2>
          </div>
          <button
            onClick={onClose}
            data-testid="help-mobile-close-btn"
            aria-label="Fermer l'aide"
            className="p-2 rounded-full"
            style={{ color: T.muted }}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Accordion list */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2.5 pb-24">
          {sections.map((s) => (
            <Accordion
              key={s.id}
              section={s}
              open={openId === s.id}
              onToggle={() => setOpenId(openId === s.id ? null : s.id)}
            />
          ))}

          {/* Glossaire */}
          <div
            className="rounded-2xl p-4"
            style={{ background: T.bg, border: `1px solid ${T.border}` }}
          >
            <div className="flex items-baseline gap-2 mb-3">
              <span className="text-lg" aria-hidden>🎨</span>
              <div>
                <h3 className="text-sm" style={{ color: T.ink, fontFamily: 'Georgia, serif', fontWeight: 700 }}>
                  Glossaire des icônes
                </h3>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {NAVIGATION_HELP_GLOSSARY.map((g) => {
                const Icon = g.icon;
                return (
                  <div
                    key={g.label}
                    className="p-2.5 rounded-lg text-center"
                    style={{ background: T.card, border: `1px solid ${T.border}` }}
                  >
                    <Icon className="w-4 h-4 mx-auto mb-1" style={{ color: T.goldDeep }} />
                    <div className="text-[10px] font-bold" style={{ color: T.ink }}>{g.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Sticky footer */}
        <div
          className="flex-shrink-0 px-5 py-3 absolute bottom-0 left-0 right-0"
          style={{ background: T.bg, borderTop: `1px solid ${T.border}` }}
        >
          <button
            onClick={onClose}
            data-testid="help-mobile-footer-close"
            className="w-full py-3 rounded-full text-sm font-bold"
            style={{
              background: `linear-gradient(135deg, ${T.goldDeep}, ${T.gold}, ${T.goldLight})`,
              color: T.ink,
            }}
          >
            J'ai compris
          </button>
        </div>
      </div>
    </div>
  );
}
